import {appClient, Scope} from '@wix/app-settings-client';
import {IWidgetControllerConfig} from '@wix/native-components-infra';

import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {APP_SETTINGS_CDN} from '@wix/wixstores-client-core/dist/es/src/constants';
import {BaseController} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/controller-factory/BaseController';
import {withErrorReporting} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/errorReporter';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {ProductPagePanoramaTransactions, UserInputType} from '../constants';
import {wixCodeGetProduct} from '../services/getProduct';
import {SPECS} from '../specs';
import {IProductPageControllerConfig} from '../types/app-types';
import {
  OnChoiceSelectedCallbackType,
  OnQuantityChangedCallbackType,
  OverrideCallback,
  ProductPageStore,
} from './ProductPageStore';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;

export class OldProductPageController extends BaseController {
  private productPageStore: ProductPageStore;
  private config: IProductPageControllerConfig;

  private readonly isResponsive: boolean;
  private readonly controllerConfig: IWidgetControllerConfig;
  private onReadyResolver: ResolveFn<void>;
  private readonly waitUntilReady: Promise<void>;

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    const isMobile = this.siteStore.isMobile();

    /* istanbul ignore next: todo: test */
    this.isResponsive = controllerParams.controllerConfig.config.style.styleParams?.booleans?.responsive || isMobile;
    this.controllerConfig = controllerParams.controllerConfig;
    this.config = _.clone(controllerParams.controllerConfig.config);
    const isEditor = typeof window !== 'undefined' && window.Wix;

    this.waitUntilReady = new Promise<void>((resolve) => {
      this.onReadyResolver = resolve;
    });

    /* istanbul ignore else: todo(flow-editor): test */
    if (isEditor) {
      this.listenToAppSettingsUpdate();
    }
  }

  private listenToAppSettingsUpdate() {
    const appSettingsClient = appClient({scope: Scope.COMPONENT, cdnUrl: APP_SETTINGS_CDN});
    appSettingsClient.onChange((pb) => {
      this.productPageStore.updateState(this.config, {
        APP: undefined,
        COMPONENT: {},
        appSettings: pb,
      });
    });
  }

  public exports = () =>
    withErrorReporting(this.flowAPI.reportError)({
      getProduct: () => {
        const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeGetProduct;
        this.flowAPI.panoramaClient.transaction(panoramaTransaction).start();
        const onSuccess = () => {
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        };
        return wixCodeGetProduct(this.siteStore, this.flowAPI.reportError, onSuccess);
      },
      getCustomTextFieldsValues: (): string[] => {
        const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeGetCustomTextFieldValues;
        this.flowAPI.panoramaClient.transaction(panoramaTransaction).start();
        const customTextFieldsValues = this.productPageStore.userInputs.text;
        this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        return customTextFieldsValues;
      },
      ...(this.siteStore.experiments.enabled(SPECS.PRODUCT_PAGE_NEW_WIX_CODE_API) && {
        // eslint-disable-next-line sonarjs/no-identical-functions
        getCustomText: async (): Promise<string[]> => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeGetCustomText;
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).start();
          const customTextFieldsValues = this.productPageStore.customText;
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
          return customTextFieldsValues;
        },
        getQuantity: async () => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeGetQuantity;
          const selectedQuantity = this.productPageStore.selectedQuantity;
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
          return selectedQuantity;
        },
        setQuantity: async (value: number) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeSetQuantity;
          this.productPageStore.handleUserInput(UserInputType.Quantity, value, 0);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
        onQuantityChanged: async (callback: OnQuantityChangedCallbackType) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeOnQuantityChanged;
          this.productPageStore.setOnQuantityChangedCallback(callback);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
        getSelectedVariantId: async () => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeGetSelectedVariantId;
          const selectedVariantId = this.productPageStore.selectedVariant?.id;
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
          return selectedVariantId;
        },
        getSelectedChoices: async () => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeGetSelectedChoices;
          const selectedChoices = this.productPageStore.selectedChoices;
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
          return selectedChoices;
        },
        onChoiceSelected: async (callback: OnChoiceSelectedCallbackType) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeOnChoiceSelected;
          this.productPageStore.setOnChoiceSelectedCallback(callback);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
        onAddToCart: async (callback: OverrideCallback) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeOnAddToCart;
          this.productPageStore.setOnAddToCartCallback(callback);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
        setAddToCartLabel: async (text: string) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeOnAddToCart;
          this.productPageStore.setAddToCartTextOverride(text);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
        onBuyNow: async (callback: OverrideCallback) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeOnBuyNow;
          this.productPageStore.setOnBuyNowCallback(callback);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
        setBuyNowLabel: async (text: string) => {
          await this.waitUntilReady;
          const panoramaTransaction = ProductPagePanoramaTransactions.WixCodeOnBuyNow;
          this.productPageStore.setBuyNowTextOverride(text);
          this.flowAPI.panoramaClient.transaction(panoramaTransaction).finish();
        },
      }),
    });

  public onConfigUpdate = (config: IProductPageControllerConfig) => {
    const {publicData} = config;
    this.config = _.clone(config);
    return this.productPageStore.updateState(this.config, publicData);
  };

  /* istanbul ignore next: todo test */
  public onAppSettingsUpdate = (updates: {[key: string]: any}) => {
    if (
      this.siteStore.experiments.enabled(SPECS.EDITOR_OOI) &&
      updates.scope === Scope.COMPONENT &&
      updates.source === 'app-settings'
    ) {
      this.productPageStore.updateAppSettings(updates.payload);
    }
  };

  public getFreeTexts = (): string[] => {
    return [];
  };

  public readonly load = async () => {
    const {publicData} = this.config;
    if (this.productPageStore) {
      return;
    }

    this.productPageStore = new ProductPageStore(
      this.config,
      () => ({} as any),
      publicData,
      this.setProps.bind(this),
      this.siteStore,
      this.controllerConfig.config.externalId || '',
      this.flowAPI.reportError,
      this.controllerConfig.compId,
      createSlotVeloAPIFactory(this.controllerConfig),
      this.controllerConfig,
      this.flowAPI.errorHandler,
      this.flowAPI.panoramaClient
    );

    await this.productPageStore
      .setInitialState()
      .then(() => {
        this.onReadyResolver();
      })
      .catch(this.flowAPI.reportError);
  };

  public readonly init = async () => {
    await this.load();
  };
}
